import { useRef, useState, useMemo, type ChangeEvent } from 'react'
import {
  Box, Button, Card, CardHeader, CardBody, Heading, HStack, VStack, Text,
  Input, InputGroup, InputRightElement, Select, Tabs, TabList, Tab, Badge,
  IconButton, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
} from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { ActionButtons } from '@/components/common/ActionButtons'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useAccessCheck } from '@/hooks/useAccess'
import { useThemeColors } from '@/hooks/useThemeColors'
import { AppVersionService } from '@/lib/services/appVersionService'

type TabValue = 'all' | 'trash'

export default function AppVersionsPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess, showWarning } = useToastNotification()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [bulkAction, setBulkAction] = useState('')
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)
  const [modalMessage, setModalMessage] = useState('')
  const access = useAccessCheck()

  const tableOptions: DataTableOptions = useMemo(() => ({
    dataFields: {
      application_name: { name: 'Application', withSort: true, maxWidth: '160px' },
      version: { name: 'Version', withSort: true, maxWidth: '100px' },
      build: { name: 'Build', withSort: true, maxWidth: '80px' },
      is_mandatory_label: { name: 'Mandatory', withSort: true, maxWidth: '100px' },
      rollout_percent: { name: 'Rollout %', withSort: true, maxWidth: '90px' },
      published_at: { name: 'Published', withSort: true, maxWidth: '150px' },
      updated_at: { name: 'Updated', withSort: true, maxWidth: '150px' },
    },
    dataSource: activeTab === 'trash' ? '/system-settings/archived/app-versions' : '/system-settings/app-versions',
    primaryKey: 'id',
    softDelete: true,
    displayInModal: false,
    noRecordText: 'No app versions found',
  }), [activeTab])

  const permissions = useMemo(() => ({
    can_edit: access.can_edit,
    can_delete: access.can_delete,
    can_create: access.can_create,
    can_view: access.can_view,
    can_export: access.can_export,
    can_import: access.can_import,
  }), [access])

  const tableParams = useMemo(() => ({ search: searchQuery }), [searchQuery])

  const handleConfirmBulkAction = async () => {
    const ids = tableRef.current?.getSelectedRows() || []
    if (ids.length === 0) return
    setIsBulkProcessing(true)
    try {
      if (bulkAction === 'restore') {
        await AppVersionService.bulkRestore(ids)
      } else if (activeTab === 'trash') {
        await AppVersionService.bulkDelete(ids, true)
      } else {
        await AppVersionService.bulkDelete(ids)
      }
      showSuccess('Success', 'Action completed')
      tableRef.current?.reload()
      setBulkAction('')
      onClose()
    } catch (e) {
      showError(e)
    } finally {
      setIsBulkProcessing(false)
    }
  }

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex={1} overflow="hidden" minH={0}>
          <Card flex={1} minH={0} overflow="hidden" display="flex" flexDirection="column">
            <CardHeader py={3}>
              <Heading size="md">App Versions</Heading>
              <Text fontSize="sm" color={colors.text}>Manage mobile app release versions and rollout</Text>
            </CardHeader>
            <CardHeader py={0} px={0}>
              <HStack justify="space-between" px={4}>
                <Tabs index={activeTab === 'all' ? 0 : 1} onChange={(i) => setActiveTab(i === 0 ? 'all' : 'trash')}>
                  <TabList borderBottom="none">
                    <Tab>All <Badge ml={2}>{totalRows}</Badge></Tab>
                    {totalTrash > 0 && <Tab>Trash <Badge ml={2} colorScheme="red">{totalTrash}</Badge></Tab>}
                  </TabList>
                </Tabs>
                <ActionButtons pathname="/system-settings/app-versions" createLabel="Create App Version" />
              </HStack>
            </CardHeader>
            <CardHeader py={2}>
              <HStack spacing={4}>
                {access.can_delete && (
                  <>
                    <Select size="sm" placeholder="Bulk actions" value={bulkAction} onChange={(e: ChangeEvent<HTMLSelectElement>) => setBulkAction(e.target.value)} w="200px">
                      {activeTab === 'trash' && <option value="restore">Restore</option>}
                      {activeTab === 'trash' && <option value="delete">Delete Permanently</option>}
                      {activeTab === 'all' && <option value="delete">Delete</option>}
                    </Select>
                    <Button size="sm" onClick={() => { if (!bulkAction) return showWarning('Select an action'); onOpen() }}>Apply</Button>
                  </>
                )}
                <InputGroup size="sm" maxW="320px">
                  <Input ref={searchRef} placeholder="Search version or app..." onKeyDown={(e) => e.key === 'Enter' && setSearchQuery(searchRef.current?.value || '')} />
                  <InputRightElement><IconButton aria-label="Search" icon={<Search size={16} />} size="sm" variant="ghost" onClick={() => setSearchQuery(searchRef.current?.value || '')} /></InputRightElement>
                </InputGroup>
              </HStack>
            </CardHeader>
            <CardBody p={0} mx={5} flex={1} minH={0}>
              <DataTable
                ref={tableRef}
                options={tableOptions}
                params={tableParams}
                access={permissions}
                setSubSub={(all, trashed) => { setTotalRows(all); setTotalTrash(trashed) }}
                onOpenModal={(id) => navigate(`/system-settings/app-versions/${id}`)}
              />
            </CardBody>
          </Card>
        </VStack>
      </Box>
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay /><ModalContent>
          <ModalHeader>Confirm</ModalHeader>
          <ModalBody><Text>{modalMessage || 'Proceed with bulk action?'}</Text></ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="blue" isLoading={isBulkProcessing} onClick={handleConfirmBulkAction}>Confirm</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  )
}
